<template>
    <div class="website-train" :class="{ builderMode }">
        <md-progress-bar md-mode="indeterminate" v-if="training" />
        <div class="header">
            <div class="d-flex gap-5 align-items-center">
                <template v-if="builderMode">
                    <i class="material-icons spin" v-if="state && state.state !== 'finished'">autorenew</i>
                    <i class="material-icons text-primary" v-else>public</i>
                </template>

                <IconButton :on-click="() => $emit('close')" v-else>
                    <i class="material-icons">arrow_back_ios</i>
                </IconButton>
                <div class="title">Website</div>
            </div>
            <div class="description">{{ $t("chatbotv2.populerUserQuestion") }}</div>
        </div>
        <div class="form" v-if="!started">
            <div class="d-flex gap-5">
                <div class="search-wrapper">
                    <div class="tpl-search">
                        <input type="text" :placeholder="$t('chatbotv2.enterUrlAddress')" v-model="url" @keyup.enter="scrape" />
                    </div>
                </div>
                <SupsisButton v-if="!builderMode" primary @click="scrape" :disabled="started">
                    {{ $t("chatbotv2.trainChatbot") }}
                </SupsisButton>
            </div>
            <span class="description">
                {{ $t("chatbotv2.sharingYourUrl") }}
            </span>
        </div>
        <div class="categories" v-if="started && !!state">
            <div class="category-wrapper" v-for="category of getCategories()">
                <div class="category" :class="{ 'category-empty-state': state.state !== 'finished' }">
                    <div class="switch">
                        <template v-if="state.state === 'finished'">
                            <a-switch
                                :checked="isCategoryEnabled(category)"
                                @click="() => toggleCategory(category)"
                                size="small"
                            />
                        </template>
                    </div>
                    <div class="title">{{ category }}</div>
                    <div class="description">
                        <div v-if="state.state === 'finished'" @click="showingCategory = category">
                            {{ getCategoryPages(category).length }} pages
                            <i class="material-icons">edit</i>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="select-panel-wrapper" v-if="state && state.state === 'finished' && !builderMode">
            <div class="select-panel">
                <div class="items-count">
                    <i class="material-icons">check</i>
                    <div>{{ getSelectedPageCount() }} {{ $t("chatbotv2.pageSelected") }}</div>
                </div>
                <div class="buttons">
                    <SupsisButton :disabled="training" @click="completeTrain">{{ $t("chatbotv2.trainChatbot") }}</SupsisButton>
                </div>
            </div>
        </div>
        <Modal2
            v-if="showingCategory"
            id="category-list-view-modal"
            :title="`Train the bot with pages from the &quot;${showingCategory}&quot; category`"
            :show-footer="false"
            @cancel="() => (showingCategory = null)"
        >
            <CategoryPageListView
                :category="showingCategory"
                :pages="getCategoryPages(showingCategory)"
                v-model="disabledUrls"
            />
        </Modal2>
    </div>
</template>
<script>
import Vue from "vue";
import IconButton from "../../Settings/components/IconButton.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import { sendSocketMessage, socketMessageStream } from "../../../../utils/socketStream";
import { mapState } from "vuex";
import _ from "lodash";
import Modal2 from "../../../../components/common/Modal2.vue";
import CategoryPageListView from "./CategoryPageListView.vue";
import SYSTEM_PUBSUB from "../../../../utils/systemPubSub";

export default Vue.extend({
    name: "WebsiteTrain",
    components: { CategoryPageListView, Modal2, SupsisButton, IconButton },
    props: {
        storyId: String,
        builderMode: {
            type: Boolean,
            default: false,
        },
        value: String,
    },
    data() {
        return {
            url: this.value || "",
            state: null,
            started: false,
            disabledUrls: [],
            showingCategory: null,
            training: false,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site._id,
            userId: (state) => state.auth.user?._id,
        }),
    },
    mounted() {
        this.listenSocket();
    },
    methods: {
        isValidUrl(url) {
            try {
                new URL(url);
                return true;
            } catch (e) {
                return false;
            }
        },
        completeTrain() {
            const pages = this.state?.pages || [];
            const selectedPageIds = pages
                .filter((page) => !this.disabledUrls.includes(page.url))
                .map((page) => page.id);
            this.training = true;
            sendSocketMessage("chatbot/v2 complete-train", {
                siteId: this.siteId,
                storyId: this.storyId,
                pageIds: selectedPageIds,
                isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai?.assistantId,
            });
        },
        scrape() {
            const url = this.url.startsWith("http") ? this.url : `https://${this.url}`;
            if (this.isValidUrl(url)) {
                this.url = url;
                this.started = true;
                if (this.builderMode) {
                    sendSocketMessage("chatbot/v2 scrape-website-and-create-story", {
                        siteId: this.siteId,
                        url,
                        userId: this.userId,
                        isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai?.assistantId,
                    });
                } else {
                    sendSocketMessage("chatbot/v2 train-with-website", {
                        siteId: this.siteId,
                        storyId: this.storyId,
                        url,
                        isAssistantAPI: !!this.$store.state.CB.stories.find((story) => story._id === this.storyId)?.ai?.assistantId,
                    });
                }
            } else {
                this.$message.error("Invalid URL");
            }
        },
        listenSocket() {
            this.sub = socketMessageStream.subscribe(({ cmd, data }) => {
                console.log("Command received:", cmd);
                console.log("Data received:", data);
                if (cmd === "ai_knowledge_pages_response") {
                    this.state = data;
                    this.$emit("scrapper-state", data);
                } else if (cmd === "website_chatbot_builder_state") {
                    const pages = this.state?.pages || [];
                    const selectedPageIds = pages
                        .filter((page) => !this.disabledUrls.includes(page.url))
                        .map((page) => page.id);
                    data.selectedPageIds = selectedPageIds;
                    data.socialLinks = this.state?.socialLinks || [];
                    this.$emit("state", data);
                } else if (cmd === "complete_train_status") {
                    const { success, error } = data;
                    console.log("Training status:", success, error);
                    if (success) {
                        this.$message.success("Training completed");
                        this.$emit("trained");
                    } else {
                        this.$message.error(error || "Training failed");
                    }
                    this.training = false;
                }
            });
            this.sub2 = SYSTEM_PUBSUB.stream.subscribe(({ type }) => {
                if (type === SYSTEM_PUBSUB.MESSAGE_TYPES.START_SCRAPE_WEBSITE) {
                    this.scrape();
                }
            });
        },
        getCategoryPages(category) {
            const pages = this.state?.pages || [];
            return pages.filter((page) => page.category === category);
        },
        getCategories() {
            const pages = this.state?.pages || [];
            return _.uniq(pages.map((page) => page.category));
        },
        isCategoryEnabled(category) {
            return (
                this.getCategoryPages(category)
                    .map((page) => page.url)
                    .filter((url) => !this.disabledUrls.includes(url)).length > 0
            );
        },
        getSelectedPageCount() {
            const pages = this.state?.pages || [];
            return pages.filter((page) => !this.disabledUrls.includes(page.url)).length;
        },
        toggleCategory(category) {
            const isEnabled = this.isCategoryEnabled(category);
            if (isEnabled) {
                this.disabledUrls = this.disabledUrls.concat(this.getCategoryPages(category).map((page) => page.url));
            } else {
                this.disabledUrls = this.disabledUrls.filter(
                    (url) =>
                        !this.getCategoryPages(category)
                            .map((page) => page.url)
                            .includes(url)
                );
            }
            this.disabledUrls = _.uniq(this.disabledUrls);
        },
    },
    beforeDestroy() {
        this.sub && this.sub.unsubscribe();
        this.sub2 && this.sub2.unsubscribe();
    },
    watch: {
        url(newUrl) {
            this.$emit("input", newUrl);
        },
        value(newUrl) {
            if (newUrl !== this.url) {
                this.url = newUrl;
            }
        },
    },
});
</script>

<style lang="scss">
#category-list-view-modal {
    .ant-modal-mask,
    .ant-modal-wrap {
        z-index: 999999999 !important;
    }
}
</style>
<style scoped lang="scss">
.website-train {
    display: flex;
    flex-direction: column;
    gap: 10px;
    background-image: radial-gradient(circle at 90% 130%, #ff7a00 13%, transparent 35%);

    width: 100%;
    height: 100%;

    &.builderMode {
        background-image: unset !important;

        .categories {
            width: calc(100% - 220px) !important;
        }
    }

    .md-progress-bar {
        position: absolute;
        top: 0;
        width: 100%;
    }

    .header {
        display: flex;
        flex-direction: column;
        gap: 5px;

        .title {
            color: #22272d;
            font-size: 28px;
            font-weight: 700;
            line-height: 36px;
        }

        .description {
            color: #22272d;
            font-size: 18px;
            line-height: 23px;
            margin-top: 8px;
        }
    }

    .form {
        width: 100%;

        .search-wrapper {
            width: 80%;
            background: transparent;

            .tpl-search {
                position: relative;
                width: 100%;
            }

            input {
                background: #f5f5f5;
                border: 1px solid #fff;
                border-radius: 10px;
                -webkit-box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
                box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);
                color: #22272d;
                font-size: 16px;
                height: 48px;
                line-height: 20px;
                padding-left: 52px;
                padding-right: 20px;
                -webkit-transition: border 0.3s;
                transition: border 0.3s;
                width: 100%;
                -webkit-appearance: none;
            }
        }

        .description {
            color: #979797;
            font-size: 12px;
            line-height: 16px;
            margin-top: 5px;
        }
    }

    .select-panel-wrapper {
        bottom: 10px;
        left: 36px;
        position: absolute;
        right: 36px;

        .select-panel {
            background: #22272d;
            border-radius: 15px;
            box-shadow: 0 -4px 32px 0 rgba(0, 0, 0, 0.16);
            color: #fff;
            height: 64px;
            align-items: center;
            display: flex;
            justify-content: center;

            .items-count {
                align-items: center;
                display: flex;
                justify-content: center;
                font-size: 16px;
                font-weight: 600;
                line-height: 20px;
                margin-left: 16px;
            }

            .buttons {
                align-items: center;
                display: flex;
                gap: 8px;
                justify-content: center;
                margin-left: auto;
                margin-right: 16px;
            }
        }
    }

    .categories {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        overflow-y: auto;
        position: relative;
        //height: 100%;

        .category-wrapper {
            flex: 1 1 32%;
        }

        .category {
            background: #f5f5f5;
            border-radius: 15px;
            height: 86px;
            padding: 20px 60px 0 25px;
            position: relative;

            .description {
                align-items: center;
                color: #979797;
                display: flex;
                font-size: 14px;
                line-height: 18px;
                gap: 5px;

                .material-icons {
                    display: none;
                }

                &:hover {
                    cursor: pointer;
                    color: var(--color-text-primary-hover);

                    div {
                        display: flex;
                        align-items: center;
                        gap: 5px;

                        .material-icons {
                            display: block;
                            font-size: 14px;
                        }
                    }
                }
            }

            .switch {
                display: flex;
                width: 100%;
                justify-content: flex-end;
                position: absolute;
                right: 13px;
                top: 13px;
            }

            &.category-empty-state {
                cursor: wait;

                .switch {
                    background: #dcdcdc;
                    border-radius: 20px;
                    height: 16px;
                    position: absolute;
                    right: 14px;
                    top: 14px;
                    width: 32px;
                }

                .description {
                    background: #dcdcdc;
                    border-radius: 20px;
                    height: 16px;
                    margin-top: 10px;
                    width: 70px;
                }
            }

            .title {
                color: #22272d;
                font-size: 18px;
                font-weight: 600;
                line-height: 23px;
                margin-bottom: 3px;
                overflow: hidden !important;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }
    }
}
</style>
